import { useContext } from 'react';
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  View,
} from '@ea-lab/reactive-json';

/**
 * PROJECT OVERRIDE of reactive-json's core `LabelFromValue`.
 * Registered as `element.LabelFromValue`, shadowing the lib component.
 *
 * Faithful port of the 2.x lib component (same option resolution + View render
 * path) with ONE added, opt-in option:
 *
 *   - `loose: true` → match options by String()-coerced equality instead of the
 *     strict `===`. Needed when the same id reaches the front with different JS
 *     types from different endpoints — e.g. decoupled_toolbox renders an entity
 *     reference id as a STRING on GET but echoes it as a NUMBER in the save
 *     response, so a strict lookup empties the label after a save until reload.
 *
 * Default (option omitted) = strict `===`, behaviourally identical to the lib →
 * zero impact on any existing usage.
 *
 * Maintenance: keep the resolution + render path in sync with
 * lib/component/element/html/LabelFromValue.js; `loose` is the only Orbit layer.
 */
export const LabelFromValue = ({ currentData, datafield, path, props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const dynamicOptions = props.dynamicOptions ?? undefined;
  const options = dynamicOptions
    ? (evaluateTemplateValue({ valueToEvaluate: dynamicOptions, globalDataContext, templateContext }) ?? [])
    : (props.options ?? []);

  const defaultFieldValue = props.defaultFieldValue ?? undefined;
  const dataLocation = props.dataLocation ?? undefined;
  let value;
  if (dataLocation) {
    value = evaluateTemplateValue({ globalDataContext, templateContext, valueToEvaluate: dataLocation }) ?? defaultFieldValue;
  }
  else {
    if ((templateContext.templateData[datafield] ?? undefined) === undefined) {
      if (typeof templateContext.templateData === 'object' && templateContext.templateData !== null) {
        templateContext.templateData[datafield] = defaultFieldValue;
      }
      else {
        try {
          templateContext.templateData = {};
          templateContext.templateData[datafield] = defaultFieldValue;
        }
        catch {
          /* templateData not writable — leave value undefined */
        }
      }
    }
    value = templateContext.templateData[datafield];
  }

  const matches = props.loose === true
    ? (o) => String(o.value) === String(value)
    : (o) => o.value === value;

  let found = options.find(matches);
  if (!found || !found.label) {
    if (!value) {
      return null;
    }
    found = value;
  }

  return (
    <ActionDependant {...props}>
      <View currentData={currentData} datafield={datafield} path={path} props={found.label} />
    </ActionDependant>
  );
};
